import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';
import { 
  Eraser, Pencil, RotateCcw, Check, ChevronRight, ChevronLeft, 
  Volume2, Star, Palette
} from 'lucide-react';

const DrawingCanvas = ({ items, onComplete, childId, title = "Trace & Draw!" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [brushColor, setBrushColor] = useState('#3b82f6');
  const [brushSize, setBrushSize] = useState(8);
  const [tool, setTool] = useState('pencil');

  const colors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];
  const currentItem = items[currentIndex];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      clearCanvas();
      drawTemplate();
    }
  }, [currentIndex]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawTemplate();
  };

  const drawTemplate = () => {
    const canvas = canvasRef.current;
    if (!canvas || !currentItem) return;
    const ctx = canvas.getContext('2d');
    
    // Draw the template letter/number as light guide
    ctx.font = 'bold 180px Fredoka, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#e2e8f0';
    ctx.fillText(currentItem.character, canvas.width / 2, canvas.height / 2);
    
    // Draw dotted outline for tracing
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.strokeText(currentItem.character, canvas.width / 2, canvas.height / 2);
    ctx.setLineDash([]);
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : brushColor;
    ctx.lineWidth = tool === 'eraser' ? brushSize * 3 : brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  const handleComplete = () => {
    if (!completed.includes(currentIndex)) {
      setCompleted([...completed, currentIndex]);
      toast.success(`Great job drawing ${currentItem.character}!`);
    }
    
    if (currentIndex < items.length - 1) {
      setCurrentIndex(i => i + 1);
    } else {
      // All items completed
      const score = Math.round((completed.length + 1) / items.length * 100);
      if (onComplete) {
        onComplete(score, items.length);
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(i => i + 1);
    }
  };

  if (completed.length === items.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-blue-800 mb-2">Amazing Drawing!</h2>
        <p className="text-xl text-blue-600 mb-4">You traced all {items.length} characters!</p>
        <Button 
          onClick={() => {
            setCompleted([]);
            setCurrentIndex(0);
          }} 
          variant="outline" 
          className="gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Draw Again
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4" data-testid="drawing-canvas">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-fredoka text-xl font-bold text-blue-800">{title}</h3>
          <p className="text-sm text-slate-500">
            {currentIndex + 1} of {items.length} • {completed.length} completed
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => speakText(`Trace the ${currentItem.type}: ${currentItem.character}`)}
          className="text-blue-600"
        >
          <Volume2 className="w-5 h-5" />
        </Button>
      </div>

      {/* Current Character Info */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-4xl font-bold text-blue-600 mr-4">{currentItem.character}</span>
              <span className="text-slate-600">{currentItem.name}</span>
            </div>
            <div className="text-sm text-slate-500">
              {currentItem.example && `Example: ${currentItem.example}`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tools */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant={tool === 'pencil' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTool('pencil')}
            className="gap-1"
          >
            <Pencil className="w-4 h-4" /> Draw
          </Button>
          <Button
            variant={tool === 'eraser' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTool('eraser')}
            className="gap-1"
          >
            <Eraser className="w-4 h-4" /> Erase
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearCanvas}
            className="gap-1"
          >
            <RotateCcw className="w-4 h-4" /> Clear
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-slate-500" />
          {colors.map(color => (
            <button
              key={color}
              onClick={() => setBrushColor(color)}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${
                brushColor === color ? 'scale-125 border-slate-800' : 'border-transparent'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* Canvas */}
      <Card className="overflow-hidden">
        <canvas
          ref={canvasRef}
          width={400}
          height={400}
          className="w-full touch-none cursor-crosshair bg-white"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          data-testid="drawing-area"
        />
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> Previous
        </Button>
        
        <Button
          onClick={handleComplete}
          className="bg-green-500 hover:bg-green-600 gap-1"
          data-testid="complete-drawing-btn"
        >
          <Check className="w-4 h-4" /> Done
        </Button>
        
        <Button
          variant="outline"
          onClick={handleNext}
          disabled={currentIndex === items.length - 1}
          className="gap-1"
        >
          Next <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default DrawingCanvas;
